// SPDX-License-Identifier: AGPL-3.0-or-later

export interface StatusGeometry {
	size: number;
	cx: number;
	cy: number;
	radius: number;
	borderWidth: number;
	isMobile?: boolean;
	phoneWidth?: number;
	phoneHeight?: number;
}

const STATUS_GEOMETRY: Record<number, StatusGeometry> = {
	16: {size: 10, cx: 13, cy: 13, radius: 5, borderWidth: 0, isMobile: false},
	20: {size: 10, cx: 17, cy: 17, radius: 6, borderWidth: 1, isMobile: false},
	24: {size: 10, cx: 20, cy: 20, radius: 7, borderWidth: 2, isMobile: false},
	32: {size: 10, cx: 27, cy: 27, radius: 8, borderWidth: 3, isMobile: false},
	36: {size: 10, cx: 30, cy: 30, radius: 8, borderWidth: 3, isMobile: false},
	40: {size: 12, cx: 34, cy: 34, radius: 9, borderWidth: 3, isMobile: false},
	44: {size: 14, cx: 38, cy: 38, radius: 10, borderWidth: 3, isMobile: false},
	48: {size: 14, cx: 42, cy: 42, radius: 10, borderWidth: 3, isMobile: false},
	56: {size: 16, cx: 49, cy: 49, radius: 11, borderWidth: 3, isMobile: false},
	80: {size: 16, cx: 68, cy: 68, radius: 11.2, borderWidth: 3.2, isMobile: false},
	120: {size: 24, cx: 100, cy: 100, radius: 16.8, borderWidth: 4.8, isMobile: false},
};

const STATUS_GEOMETRY_MOBILE: Record<number, StatusGeometry> = {
	16: {size: 10, cx: 13, cy: 13, radius: 5, borderWidth: 0, isMobile: true, phoneWidth: 10, phoneHeight: 16},
	20: {size: 10, cx: 17, cy: 17, radius: 6, borderWidth: 1, isMobile: true, phoneWidth: 10, phoneHeight: 16},
	24: {size: 10, cx: 20, cy: 20, radius: 7, borderWidth: 2, isMobile: true, phoneWidth: 10, phoneHeight: 16},
	32: {size: 10, cx: 27, cy: 27, radius: 8, borderWidth: 3, isMobile: true, phoneWidth: 10, phoneHeight: 16},
	36: {size: 10, cx: 30, cy: 30, radius: 8, borderWidth: 3, isMobile: true, phoneWidth: 10, phoneHeight: 16},
	40: {size: 12, cx: 34, cy: 34, radius: 9, borderWidth: 3, isMobile: true, phoneWidth: 12, phoneHeight: 18},
	44: {size: 14, cx: 38, cy: 38, radius: 10, borderWidth: 3, isMobile: true, phoneWidth: 14, phoneHeight: 20},
	48: {size: 14, cx: 42, cy: 42, radius: 10, borderWidth: 3, isMobile: true, phoneWidth: 14, phoneHeight: 20},
	56: {size: 16, cx: 49, cy: 49, radius: 11, borderWidth: 3, isMobile: true, phoneWidth: 16, phoneHeight: 24},
	80: {size: 16, cx: 68, cy: 68, radius: 11.2, borderWidth: 3.2, isMobile: true, phoneWidth: 16, phoneHeight: 24},
	120: {size: 24, cx: 100, cy: 100, radius: 16.8, borderWidth: 4.8, isMobile: true, phoneWidth: 24, phoneHeight: 34},
};

export function getStatusGeometry(avatarSize: number, isMobile: boolean = false): StatusGeometry {
	const map = isMobile ? STATUS_GEOMETRY_MOBILE : STATUS_GEOMETRY;

	if (map[avatarSize]) {
		return map[avatarSize];
	}

	const sizes = Object.keys(map)
		.map(Number)
		.sort((a, b) => a - b);
	const firstSize = sizes[0];
	const lastSize = sizes[sizes.length - 1];

	if (avatarSize <= firstSize) {
		return scaleGeometry(map[firstSize], avatarSize / firstSize, isMobile);
	}
	if (avatarSize >= lastSize) {
		return scaleGeometry(map[lastSize], avatarSize / lastSize, isMobile);
	}

	const upperSize = sizes.find((size) => size > avatarSize) ?? lastSize;
	const lowerSize = sizes[sizes.indexOf(upperSize) - 1] ?? firstSize;
	const progress = (avatarSize - lowerSize) / (upperSize - lowerSize);
	return interpolateGeometry(map[lowerSize], map[upperSize], progress, isMobile);
}

function interpolateGeometry(from: StatusGeometry, to: StatusGeometry, progress: number, isMobile: boolean): StatusGeometry {
	return {
		size: interpolate(from.size, to.size, progress),
		cx: interpolate(from.cx, to.cx, progress),
		cy: interpolate(from.cy, to.cy, progress),
		radius: interpolate(from.radius, to.radius, progress),
		borderWidth: interpolate(from.borderWidth, to.borderWidth, progress),
		isMobile,
		phoneWidth:
			from.phoneWidth != null && to.phoneWidth != null ? interpolate(from.phoneWidth, to.phoneWidth, progress) : undefined,
		phoneHeight:
			from.phoneHeight != null && to.phoneHeight != null
				? interpolate(from.phoneHeight, to.phoneHeight, progress)
				: undefined,
	};
}

function scaleGeometry(geometry: StatusGeometry, scale: number, isMobile: boolean): StatusGeometry {
	return {
		size: geometry.size * scale,
		cx: geometry.cx * scale,
		cy: geometry.cy * scale,
		radius: geometry.radius * scale,
		borderWidth: geometry.borderWidth * scale,
		isMobile,
		phoneWidth: geometry.phoneWidth == null ? undefined : geometry.phoneWidth * scale,
		phoneHeight: geometry.phoneHeight == null ? undefined : geometry.phoneHeight * scale,
	};
}

function interpolate(from: number, to: number, progress: number): number {
	return from + (to - from) * progress;
}
